/**
 * Created by piotr.pozniak@thebeaverhead.com on 21/01/2021.
 */

import { RRule } from "rrule";
import { CT } from "../consts";
import {
  firstDayOfWeek,
  getEndOfMonth,
  getEndOfWeek,
  getEventsDateRange,
  getStartOfMonth,
} from "./date";

/**
 * Checks whether current calendar is in allowed calendars.
 * @param current
 * @param allowed
 * @returns {boolean}
 */
export const isCTAllowed = (current, allowed) => {
  return allowed.indexOf(current) >= 0;
};

const tzOffset = new Date().getTimezoneOffset();

export const toRRuleInput = (date) => {
  date.setMinutes(date.getMinutes() - tzOffset);
  return date;
};

export const fromRRuleOutput = (date) => {
  const dstOffset = date.getTimezoneOffset() - tzOffset;
  date.setMinutes(date.getMinutes() + tzOffset + dstOffset);
  return date;
};

/**
 *
 * @param rule
 * @returns {null|RRule}
 */
export const cleanRRule = (options) => {
  const initialRule = new RRule({
    ...options,
    dtstart: toRRuleInput(options.dtstart),
  });

  if (initialRule.options.until || initialRule.options.count) {
    if (initialRule.count() <= 1) {
      return null;
    }
  }

  // bug in RRULE https://github.com/jakubroztocil/rrule/issues/556#issuecomment-1264889494
  const nextOccurrence = fromRRuleOutput(initialRule.after(options.dtstart));

  if (options.until && nextOccurrence < options.until) {
    // TODO: this is a workaround for the bug in RRULE library where it will not show events happening
    // on the same day as until date, e.g. Rule is "until 17 Jan" and event is on 17 Jan, it will not show
    options.until.setHours(23, 59, 59, 999);

    // Adjust time to UTC considering Daylight Saving Time
    options.until = new Date(
      (options.until.getUnixTime() - options.until.getTimezoneOffset() * 60) *
        1000
    );
  }

  const cleanOptions = {
    ...options,
    dtstart: toRRuleInput(nextOccurrence),
  };

  return new RRule(cleanOptions);
};

/**
 *
 * @param event
 * @returns {*|undefined}
 */
export const getOriginEventPageUrl = (event) => {
  return event?.ctas?.eventPage?.url || undefined;
};

/**
 *
 * @param event
 * @param newTab
 */
export const openEventPage = (event, newTab) => {
  const originUrl = getOriginEventPageUrl(event);
  if (originUrl) {
    window.open(originUrl, newTab ? event.slug : "_self");
  }
};

export const isValidEmail = (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);

/**
 * Get date range from option and value. Use this to translated the Number of events "show only events within this time range"
 * into a date range.
 * @param option
 * @param value
 * @returns {[Date,Date]|[null,null]}
 */
export const getDateRange = (option, value) => {
  if (option == "date_range") {
    if (value && value.indexOf(":") > -1) {
      const [start, end] = value.split(":");
      const startDate = new Date(start);
      const endDate = new Date(end);

      return [startDate, endDate];
    }
  }

  return [null, null];
};

/**
 * Sorts events with all-day events first, then by start_time
 * @param a
 * @param b
 * @returns {number}
 */
export const sortEventsWithAllDayFirst = (a, b) => {
  if (a.allDay && !b.allDay) {
    return -1; // eventA comes first
  } else if (!a.allDay && b.allDay) {
    return 1; // eventB comes first
  } else {
    // Both are either all-day events or neither are, so sort by start_time
    return a.start_time - b.start_time;
  }
};

/**
 *
 * @param filters
 * @param stateSelectedDate
 * @param eventsNumberOption
 * @param eventsNumberCount
 * @param calendarTemplate
 * @param currentPage
 * @returns {{endDate: Date, limit: null, page: null, filters, startDate: Date}|{endDate: string, limit: null, page: null, filters, startDate: string}|{endDate: null, limit, page: null, filters, startDate: string}|{endDate: null, limit: null, page: null, filters, startDate: null}|boolean}
 */
export const getFetchEventsParams = (
  filters,
  stateSelectedDate,
  eventsNumberOption,
  eventsNumberCount,
  calendarTemplate,
  currentPage = 1
) => {
  if (
    eventsNumberOption !== "date_range" &&
    (isCTAllowed(calendarTemplate, [CT.weekly]) ||
      eventsNumberOption === "current_week")
  ) {
    let startDate = firstDayOfWeek(new Date(stateSelectedDate));

    if (eventsNumberOption === "upcoming") {
      startDate = stateSelectedDate < new Date() ? new Date() : startDate;
    }

    startDate = new Date(
      startDate.getUnixTime() * 1000 - startDate.getTimezoneOffset() * 60 * 1000
    );
    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: new Date(new Date(startDate).getTime() + 6 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      limit: undefined,
      page: undefined,
      filters,
    };
  }

  if (eventsNumberOption === "date_range") {
    if (!eventsNumberCount || eventsNumberCount.indexOf(":") === -1) {
      return {
        startDate: undefined,
        endDate: undefined,
        limit: undefined,
        page: undefined,
        filters,
      };
    }
    const [rangeStartDateStr, rangeEndDateStr] = eventsNumberCount.split(":");

    const startDate =
      calendarTemplate === CT.weekly
        ? firstDayOfWeek(new Date(stateSelectedDate))
        : new Date(stateSelectedDate);

    const endDate =
      calendarTemplate === CT.weekly
        ? getEndOfWeek(startDate)
        : // TODO verify this is ok
          new Date(rangeEndDateStr); //getEndOfMonth(startDate);

    const type = calendarTemplate === CT.weekly ? "week" : "month";

    const rangeStartDate = new Date(rangeStartDateStr);
    const rangeEndDate = new Date(rangeEndDateStr);

    // console.log(startDate, rangeStartDate);
    // console.log(endDate, rangeEndDate);

    if (startDate > rangeEndDate || endDate < rangeStartDate) {
      return false;
    }

    const [adjustedStartDate, adjustedEndDate] = getEventsDateRange(
      startDate,
      endDate,
      rangeStartDate,
      rangeEndDate,
      type
    );

    return {
      startDate: adjustedStartDate.toISOString().split("T")[0],
      endDate: adjustedEndDate.toISOString().split("T")[0],
      limit: undefined,
      page: undefined,
      filters,
    };
  }

  if (eventsNumberOption === "upcoming-limit") {
    return {
      startDate: new Date(),
      endDate: undefined,
      limit: eventsNumberCount,
      page: currentPage,
      filters,
    };
  }

  if (eventsNumberOption === "all") {
    return {
      startDate: getStartOfMonth(new Date(stateSelectedDate)),
      endDate: getEndOfMonth(stateSelectedDate),
      limit: undefined,
      page: undefined,
      filters,
    };
  }

  if (eventsNumberOption === "upcoming") {
    let startDate = new Date(stateSelectedDate);

    if (startDate < new Date()) {
      startDate = new Date();
    } /* if (calendarTemplate === CT.monthly) */ else {
      if (!startDate.isSameMonth(new Date())) {
        // console.log("defaulting to beginning of the month");
        startDate = getStartOfMonth(stateSelectedDate);
      } else {
        // console.log("defaulting to today");
        startDate = new Date();
      }
    }

    const endDate = getEndOfMonth(stateSelectedDate);

    return {
      startDate,
      endDate,
      limit: undefined,
      page: undefined,
      filters,
    };
  }
};

/**
 * Get selected date based on eventsNumberOption and eventsNumberCount
 * @param eventsNumberOption
 * @param eventsNumberCount
 * @returns {Date}
 */
export const getSelectedDate = (eventsNumberOption, eventsNumberCount) => {
  if (eventsNumberOption === "date_range") {
    if (!eventsNumberCount || eventsNumberCount.indexOf(":") === -1) {
      return new Date();
    }
    const [startDate] = eventsNumberCount.split(":");
    return new Date(startDate);
  }

  return new Date();
};

/**
 *
 * @param calendarUUID
 * @param eventSlug
 * @param eventName
 * @param params
 * @returns {`/${*}/event/${string}`}
 */
export const getSharedEventUrl = (
  calendarUUID,
  eventSlug,
  eventName,
  params = {}
) => {
  const url = new URL(window.location.href);
  url.searchParams.has("calendar") && url.searchParams.delete("calendar");
  url.searchParams.append("calendar", calendarUUID);
  url.searchParams.has("event") && url.searchParams.delete("event");
  url.searchParams.append("event", eventSlug + "----" + eventName);

  Object.keys(params).map((key) => {
    if (!params[key]) {
      return;
    }
    url.searchParams.has(key) && url.searchParams.delete(key);
    url.searchParams.append(key, params[key]);
  });

  return url.pathname + "?" + url.searchParams.toString();
};

/**
 *
 */
export const removeEventUrlQueryParams = (params) => {
  const url = new URL(window.location.href);

  params.map((param) => {
    url.searchParams.has(param) && url.searchParams.delete(param);
  });

  return url.pathname + "?" + url.searchParams.toString();
};
